import React, { useState } from 'react';
import {
  BooleanInput,
  Button,
  FormDataConsumer,
  FormWithRedirect,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
  useUpdate,
} from 'react-admin';
import moment from 'moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { Box, TextField } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconCancel from '@material-ui/icons/Cancel';
import HomeIcon from '@material-ui/icons/Home';
import DomainIcon from '@material-ui/icons/Domain';
import dataProvider from '../../dataProvider';
import ReferenceChild from '../../components/ReferenceChild';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import { getZ3PConfigValue, perPage } from '../../helper';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#fdfdfd',
    },
    '& .MuiFormHelperText-contained': {
      display: 'none',
    },
  },
  btnCancel: {
    backgroundColor: '#fdfdfd',
    height: 37,
    marginRight: 10,
    padding: 10,
    color: '#212B36',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    borderRadius:8
  },
  serviceTile: {
    background: '#ebebeb',
    padding: 5,
    borderRadius: 5,
    marginRight: 10,
  },
  pickerSapce: {
    marginTop: 8,
    marginBottom: 4,
    '& .MuiOutlinedInput-notchedOutline':{
      border: '2px solid rgba(145, 158, 171, 0.2)',
    },
    '& .MuiOutlinedInput-root':{
      borderRadius:8
    },
    '& input':{
      padding: '22px 20px'
    }
  },
  mainBox:{
     marginBottom: 20,
     marginTop:9,
     '& h5': {
      fontWeight:700
     }
  },
  boxMargin: {
    marginBottom:16
  },
  iconMargin: {
    marginRight: 16,
  },
  listColor: {
    color: '#212B36',
    marginBottom:8
  },
  boxWidth:{
    width:'50%'
  },
  w100:{
    width: '100%'
  },
  actionBox:{
    position:'absolute',
    top:17,
    right:12
  }
}));

// The interval to be used for arrival and departure time inputs
const INPUT_TIME_INTERVAL_IN_SECONDS = 15 * 60;

const serviceLocations = getZ3PConfigValue('service_locations');
const hasWorkplaceServices = serviceLocations?.includes('workplace');
const z3pClient = getZ3PConfigValue('z3p_client_name');

const UnifiedEditBasics = ({ setIsEditing, ...rest }) => {
  const { record } = rest;
  const [customerID, setCustomerID] = useState(null);
  const [date, setDate] = useState(record.date);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const getCustomerAddresses = (customer_id) => {
    return dataProvider
      .getOne('customer', {
        id: customer_id,
      })
      .then((response) => {
        const { data } = response;
        const { addresses } = data;
        return { addresses };
      });
  };
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { loading: isSaving }] = useUpdate('scheduled-work');
  const handleDate = (changedDate) => {
    setDate(moment(changedDate).format('YYYY-MM-DD'));
  };
  // TODO: These validation errors aren't actually showing the message, because of our custom FormWithRedirect.
  // But they do show the red line.
  const validateScheduledWork = (values) => {
    const errors = {};
    // Must have either client_location_id or customer_address_id
    if (!values.client_location_id && !values.customer_address_id) {
      errors.client_location_id =
        'resources.scheduled-work.validation.location';
      errors.customer_address_id =
        'resources.scheduled-work.validation.location';
    }
    return errors;
  };
  const handleSubmit = async (values) => {
    // If there's a customer address, clear out the client location.
    if (values.customer_address_id) {
      values.client_location_id = null;
      values.keyosk_id = null;
      values.parking_id = null;
      delete values.box; // Can't send a null box
    }
    update(
      {
        payload: {
          id: record.id,
          data: { ...values, date },
          previousData: record,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          notify('Basics updated successfully');
          refresh();
        },
        onFailure: ({ error }) => {
          notify(error.message, 'error');
        },
      },
    );
  };

  const timeFormatter = (time) => {
    // Remove seconds from the time
    return (time || '').slice(0, -3);
  };

  const timeParser = (time) => {
    // Parse Proper Time Format
    if (time) {
      return `${time}:00`;
    }

    return time;
  };

  const schedulingSystem = getZ3PConfigValue('scheduling_system');

  const classes = useStyles();
  const isShopService = record.location_type === 'shop';
  return (
    <>
      <Grid container spacing={0} className={classes.mainBox}>
        <Grid item md={12}>
          <Typography variant="h5" gutterBottom>
            Appointment Details
          </Typography>
        </Grid>
      </Grid>
      <FormWithRedirect
        resource="scheduled-work"
        record={record}
        save={handleSubmit}
        validate={validateScheduledWork}
        render={(formProps) => (
          <>
            <Grid container spacing={1} className={`${classes.root} schedule_work_section`}>
              <Grid item lg={4} md={4} sm={4} xs={4} className={classes.actionBox}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                >
                  <Button
                    label="ra.action.cancel"
                    onClick={() => setIsEditing(false)}
                    className={classes.btnCancel}
                  >
                    <IconCancel />
                  </Button>
                  <SaveButton
                    handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                    saving={isSaving}
                    disabled={isSaving || (date === record.date && formProps.pristine)}
                  />
                </Box>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Box display="flex" my={1} className={classes.boxMargin}>
                  <Box>
                    <svg xmlns="http://www.w3.org/2000/svg" className={classes.iconMargin} width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M21 17C21 18.0609 20.5786 19.0783 19.8284 19.8284C19.0783 20.5786 18.0609 21 17 21C15.9391 21 14.9217 20.5786 14.1716 19.8284C13.4214 19.0783 13 18.0609 13 17C13 15.9391 13.4214 14.9217 14.1716 14.1716C14.9217 13.4214 15.9391 13 17 13M21 17C21 15.9391 20.5786 14.9217 19.8284 14.1716C19.0783 13.4214 18.0609 13 17 13M21 17H17V13M12 3V7C12 7.26522 12.1054 7.51957 12.2929 7.70711C12.4804 7.89464 12.7348 8 13 8H17M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H11.5M12 3L17 8M17 8V10" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </Box>
                  <Box className={`${classes.boxWidth} filter_items`}>
                    <Typography variant="body2" className={classes.listColor}>
                      <span className={classes.listLabel}>Status</span>
                    </Typography>
                    {record.status === 'cancelled' ? (
                      <Box mt={2.8} mb={0}>
                        <Typography variant="body1" gutterBottom>
                          <b>Status:</b> Cancelled
                        </Typography>
                      </Box>
                    ) : (
                      <SelectInput
                        source="status"
                        choices={[
                          { id: 'scheduled', name: 'Scheduled' },
                          ...(record.st_order_id ? [] : [{ id: 'keys_available', name: 'Keys Available' }]),
                          ...(record.st_order_id ? [] : [{ id: 'suggested', name: 'Suggested' }]),
                        ]}
                      />
                    )}
                  </Box>
                </Box>
                
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Box display="flex" my={1} className={classes.boxMargin}>
                  <Box>
                    <svg className={classes.iconMargin} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M6.99985 9.99985H9.99985V6.99985L6.49985 3.49985C7.61948 2.96513 8.87735 2.79066 10.1003 3.00048C11.3232 3.21029 12.4509 3.79407 13.3283 4.67143C14.2056 5.54878 14.7894 6.67656 14.9992 7.89946C15.209 9.12235 15.0346 10.3802 14.4999 11.4999L20.4999 17.4999C20.8977 17.8977 21.1212 18.4372 21.1212 18.9999C21.1212 19.5625 20.8977 20.102 20.4999 20.4999C20.102 20.8977 19.5625 21.1212 18.9999 21.1212C18.4372 21.1212 17.8977 20.8977 17.4999 20.4999L11.4999 14.4999C10.3802 15.0346 9.12235 15.209 7.89946 14.9992C6.67656 14.7894 5.54878 14.2056 4.67143 13.3283C3.79407 12.4509 3.21029 11.3232 3.00048 10.1003C2.79066 8.87735 2.96513 7.61948 3.49985 6.49985L6.99985 9.99985Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </Box>
                  <Box className={`${classes.boxWidth} filter_items`}>
                    <Typography variant="body2" className={classes.listColor}>
                      <span className={classes.listLabel}>Service bay or vehicle</span>
                    </Typography>
                    <ReferenceInput
                      source="service_vehicle_id"
                      reference="service-vehicle"
                      sort={{ field: 'name', order: 'ASC' }}
                      filter={{
                        is_active: true,
                        is_service_bay: isShopService,
                      }}
                      perPage={perPage}
                      label={isShopService ? 'Service Bay' : 'Service Vehicle'}
                    >
                      <ReferenceChild
                        {...rest}
                        optionText="name"
                        source="service_vehicle_id"
                      />
                    </ReferenceInput>
                  </Box>
                </Box>
              </Grid>
              {customerAddresses.length > 0 && (
              <>
              <Grid item xs={12} sm={12} md={12}>
                <Box display="flex" my={1} className={classes.boxMargin}>
                  <Box>
                    <svg className={classes.iconMargin}  xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M11.8698 21.48C11.3858 21.4493 10.9296 21.2431 10.5868 20.9L6.34279 16.657C5.34665 15.6604 4.63141 14.4183 4.26944 13.0565C3.90746 11.6947 3.9116 10.2615 4.28144 8.90177C4.65127 7.54207 5.37368 6.30418 6.37556 5.31334C7.37745 4.3225 8.62327 3.61386 9.98698 3.25912C11.3507 2.90439 12.7839 2.91613 14.1416 3.29318C15.4993 3.67023 16.7334 4.39919 17.7189 5.40632C18.7044 6.41345 19.4064 7.66302 19.7539 9.02859C20.1014 10.3942 20.082 11.8273 19.6978 13.183M14.9998 19L16.9998 21L20.9998 17M8.99976 11C8.99976 11.7956 9.31583 12.5587 9.87844 13.1213C10.441 13.6839 11.2041 14 11.9998 14C12.7954 14 13.5585 13.6839 14.1211 13.1213C14.6837 12.5587 14.9998 11.7956 14.9998 11C14.9998 10.2044 14.6837 9.44129 14.1211 8.87868C13.5585 8.31607 12.7954 8 11.9998 8C11.2041 8 10.441 8.31607 9.87844 8.87868C9.31583 9.44129 8.99976 10.2044 8.99976 11Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </Box>
                  
                  <Box className={`${classes.boxWidth} filter_items`}>
                    <Typography variant="body1" gutterBottom className={classes.listLabel}>
                      Location address
                    </Typography>
                    <SelectInput
                      allowEmpty
                      optionText="one_line_human_readable_address"
                      choices={customerAddresses}
                      source="customer_address_id"
                    />
                  </Box>
                </Box>
              </Grid>
              </>
            )}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box display="flex" my={1} className={classes.boxMargin}>
                <Box>
                  <svg className={classes.iconMargin} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11.795 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V7C3 6.46957 3.21071 5.96086 3.58579 5.58579C3.96086 5.21071 4.46957 5 5 5H17C17.5304 5 18.0391 5.21071 18.4142 5.58579C18.7893 5.96086 19 6.46957 19 7V11H3M15 3V7M7 3V7M18 16.4961V18.0001L19 19.0001M14 18C14 19.0609 14.4214 20.0783 15.1716 20.8284C15.9217 21.5786 16.9391 22 18 22C19.0609 22 20.0783 21.5786 20.8284 20.8284C21.5786 20.0783 22 19.0609 22 18C22 16.9391 21.5786 15.9217 20.8284 15.1716C20.0783 14.4214 19.0609 14 18 14C16.9391 14 15.9217 14.4214 15.1716 15.1716C14.4214 15.9217 14 16.9391 14 18Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </Box>
                <Box className={classes.w100}>
                  <Typography variant="body1" gutterBottom className={classes.listLabel}>
                    Date and Time
                  </Typography>
                  <Grid container spacing={1}>
                    {schedulingSystem === 'availability_windows' && (
                    <>
                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          {/* TODO: Use CompatibleDatesCalendar from https://github.com/zippitycars/zippity-admin/pull/327/files#diff-ea8d93c4d95da22774af0df7e0a3caf5dccd508c2847c64696203c2ef228f228 */}
                          <DatePicker
                            className={classes.pickerSapce}
                            value={date}
                            onChange={handleDate}
                            format="MM/DD/yyyy"
                            label="Date"
                            TextFieldComponent={(props) => (
                              <TextField 
                                {...props} 
                                variant="outlined" // Ensure this is set to 'outlined'
                                fullWidth 
                              />
                            )}
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <TextInput
                          source="planned_arrival_time"
                          type="time"
                          format={timeFormatter}
                          parse={timeParser}
                          validate={[required()]}
                          inputProps={{
                            step: INPUT_TIME_INTERVAL_IN_SECONDS,
                          }}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} sm={6} xs={12}>
                        <TextInput
                          source="planned_departure_time"
                          type="time"
                          validate={[required()]}
                          format={timeFormatter}
                          parse={timeParser}
                          inputProps={{
                            step: INPUT_TIME_INTERVAL_IN_SECONDS,
                          }}
                        />
                      </Grid>
                  
                    </>
                    )}
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box display="none" my={1} className={classes.boxMargin}>
                <Box>
                  <svg className={classes.iconMargin} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 7H15M9 11H15M9 15H13M5 5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V5Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </Box>
                <Box>
                  <Typography variant="body1" gutterBottom className={classes.listLabel}>
                    Special instructions
                  </Typography>
                  <Typography variant="body2" className={classes.listColor}>
                    
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

            <FormDataConsumer>
              {({ formData }) => {
                if (
                  formData.customer_id &&
                  formData.customer_id !== customerID
                ) {
                  setCustomerID(formData.customer_id);
                  getCustomerAddresses(formData.customer_id).then((r) => {
                    setCustomerAddresses(r.addresses);
                  });
                }
              }}
            </FormDataConsumer>

            <Grid container spacing={1} className={classes.root}>
              <Grid item lg={6} md={6} sm={6} xs={12}>
                <FormDataConsumer>
                  {({ formData }) => {
                    if (!formData.customer_address_id && hasWorkplaceServices) {
                      return (
                        <>
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center">
                              <DomainIcon className={classes.serviceTile} />
                              <Box width="100%">
                                <ReferenceInput
                                  source="client_location_id"
                                  reference="client-location"
                                  perPage={perPage}
                                  allowEmpty
                                  label={
                                    hasWorkplaceServices
                                      ? 'Client location'
                                      : 'Shop'
                                  }
                                >
                                  <ReferenceChild
                                    {...rest}
                                    optionText="short_name"
                                    source="client_location_id"
                                  />
                                </ReferenceInput>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <FormDataConsumer source="parking_id">
                              {({ formData: innerFormData, ...props }) => (
                                <ReferenceInput
                                  source="parking_id"
                                  reference="parking"
                                  filter={{
                                    client_location_id:
                                      innerFormData.client_location_id,
                                  }}
                                  {...props}
                                >
                                  <ReferenceChild
                                    {...rest}
                                    optionText="name"
                                    source="parking_id"
                                  />
                                </ReferenceInput>
                              )}
                            </FormDataConsumer>
                          </Grid>
                          <Grid item xs={12}>
                            <FormDataConsumer source="keyosk_id">
                              {({ formData: innerFormData, ...props }) => (
                                <ReferenceInput
                                  source="keyosk_id"
                                  reference="keyosk"
                                  filter={{
                                    client_location_id:
                                      innerFormData.client_location_id,
                                  }}
                                  {...props}
                                >
                                  <ReferenceChild
                                    {...rest}
                                    optionText="name"
                                    source="keyosk_id"
                                  />
                                </ReferenceInput>
                              )}
                            </FormDataConsumer>
                          </Grid>
                          <Grid item xs={12}>
                            <TextInput source="box" />
                          </Grid>
                        </>
                      );
                    }
                    return null;
                  }}
                </FormDataConsumer>
              </Grid>
              {z3pClient === 'zippity' && (
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <Box pl={2}>
                    <Grid item xs={12}>
                      <BooleanInput source="is_fleet_service" />
                    </Grid>
                  </Box>
                </Grid>
              )}
            </Grid>
            
          </>
        )}
      />
    </>
  );
};

export default UnifiedEditBasics;
